'use client'

/**
 * REFERRAL BEACON — the missing link that made the entire partner program unable to pay anyone.
 *
 * 🚨 WHAT WAS BROKEN (measured 2026-08-16, live on prod). The attribution chain is:
 *      share link  /partners?ref=CODE
 *        -> 308    /affiliate?ref=CODE          (the ?ref DOES survive the redirect)
 *        -> /api/referral/track?ref=CODE        <- THIS STEP DID NOT EXIST
 *        -> sets httpOnly cookie `ddt_ref`
 *        -> auth.ts signIn reads that cookie and calls attributeSignup()
 *
 * Every OTHER link in that chain was built and correct: the track route sets the cookie and counts
 * the click, auth.ts:270 reads it, attributeSignup stamps referred_by, the Stripe webhook accrues
 * commission. Only the CALL to the track route was missing, and nothing anywhere read a `?ref` param.
 * Verified against the live site: /partners?ref=X redirects with the ref intact, /affiliate returns
 * 200, and NO Set-Cookie: ddt_ref appears anywhere in the chain.
 *
 * ⭐ THE TRACK ROUTE ALREADY KNEW ABOUT THIS COMPONENT. Its header says it is "Also exposed as POST
 * for fetch()-based callers (the page beacon)" — the beacon was designed, documented, and never
 * written. So a partner could have driven a hundred signups, earned nothing, and seen 0 clicks and
 * 0 signups on their dashboard, which is the worst failure this program could have: silent, and
 * indistinguishable from having no audience.
 *
 * DESIGN NOTES
 *  - Mounted in the ROOT layout, not on /affiliate, because a partner can put `?ref=` on ANY url.
 *  - Fires ONLY when a `ref` param is present, so it costs one `useSearchParams` read otherwise.
 *  - POST, matching the route's documented fetch() shape. `keepalive` so it survives an immediate
 *    client-side navigation away from the landing page.
 *  - Best-effort by construction: the cookie is what matters and the click counter is decoration.
 *    A failed beacon must never surface to a visitor, so everything is swallowed.
 *  - Deduped per code per page-load via a ref, so a re-render cannot double-count a click.
 *
 * ⚠ THE SHAPE CHECK IS SERVER-SIDE AND STAYS THERE. isValidCodeShape runs in the route; this sends
 * whatever it sees. Duplicating the validator here would let the two drift, and the route already
 * 400s an invalid code.
 */
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'

export default function ReferralBeacon() {
  const params = useSearchParams()
  const sent = useRef<string | null>(null)

  useEffect(() => {
    const code = params?.get('ref')
    if (!code) return
    // One beacon per code per mount. Re-renders and param churn cannot re-fire it.
    if (sent.current === code) return
    sent.current = code
    try {
      void fetch(`/api/referral/track?ref=${encodeURIComponent(code)}`, {
        method: 'POST',
        cache: 'no-store',
        keepalive: true,
      }).catch(() => {})
    } catch {
      /* attribution must never break a page render */
    }
  }, [params])

  return null
}
